import React, { useState } from 'react'
import gql from 'graphql-tag'
import memoize from 'lodash.memoize'
import { Grid, MaxCellHeightsByRow } from '../../legacy-design-system/grid'
import { GridLoader, GridStateManager } from '../grid-data'
import { LegacyGridContainer } from '../../legacy-design-system/legacy-main'
import { AgentFeaturesLoader } from '../agent-features'
import { AggregationConfigs, AggregationOptions } from '../aggregations'
import {
  OrderFieldConfig,
  Order,
  OrderDirection
} from '../../legacy-design-system/order'
import {
  OnGroupToggleProps,
  OnOptionToggleProps
} from '../../legacy-design-system/filter'
import {
  environmentI18nConfig,
  getRowHeightFromApplicationCount,
  maxApplicationsShownByDefault,
  Server,
  serverFragment
} from './shared'
import {
  featureSwitchLoadingCellRenderer,
  getAssessCellRenderer,
  getProtectCellRenderer
} from './feature-switch-cell'
import {
  getEnvironmentCellRenderer,
  loadingEnvironmentCell
} from './environment-cell'
import {
  firstSeenCellRenderer,
  lastSeenCellRenderer,
  loadingSeenCell
} from './seen-cell'
import {
  getApplicationsCellRenderer,
  loadingApplicationsCellRenderer
} from './applications-cell'
import {
  getServerMetadataCellRenderer,
  loadingServerMetadataCell
} from './server-metadata-cell'
import { BulkActionList } from './actions'
import { Query } from 'react-apollo'
import { getRowActionsCellRenderer } from './row-actions-cell'
import { AgentLanguage } from '../agent-language'
import { sendDefaultEvent } from '../analytics'
import { useQueryClient } from 'react-query'

const serverBatchSize = 25

const serverQuery = gql`
  query GetServer($organizationID: ID!, $serverID: ID!) {
    server(organizationID: $organizationID, serverID: $serverID) {
      ...Server
    }
  }

  ${serverFragment}
`

const serverPageQuery = gql`
  query ServerPage(
    $offset: Int!
    $limit: Int!
    $organizationID: ID!
    $orderBy: ServerOrder!
    $filterText: String
    $quickFilter: String
    $tags: [String]
    $applications: [String]
    $logLevels: [String]
    $environments: [String]
    $agentVersions: [String]
  ) {
    servers(
      offset: $offset
      limit: $limit
      organizationID: $organizationID
      orderBy: $orderBy
      filters: {
        tags: $tags
        applications: $applications
        logLevels: $logLevels
        environments: $environments
        agentVersions: $agentVersions
        filterText: $filterText
        quickFilter: $quickFilter
      }
    ) {
      items {
        ...Server
      }
      total
    }
  }

  ${serverFragment}
`

const getServerAggregationQuery = (name: string, queryFragment: string) =>
  gql`
    query Server${name}Aggregations(
      $organizationID: ID!
      $filterText: String
      $quickFilter: String
      $tags: [String]
      $applications: [String]
      $logLevels: [String]
      $environments: [String]
      $agentVersions: [String]
    ) {
      serverAggregations(
        organizationID: $organizationID
        filters: {
          tags: $tags
          applications: $applications
          logLevels: $logLevels
          environments: $environments
          agentVersions: $agentVersions
          filterText: $filterText
          quickFilter: $quickFilter
        }
      ) {
        ${queryFragment}
      }
    }
  `

const aggregationQueries = [
  getServerAggregationQuery(
    'Tag',
    `tags {
          count
          key
        }`
  ),
  getServerAggregationQuery(
    'Applications',
    `applications {
    count
    key {
      id
      name
    }
  }`
  ),
  getServerAggregationQuery(
    'LogLevel',
    `logLevels {
    count
    key
  }`
  ),
  getServerAggregationQuery(
    'Environment',
    `environments {
    count
    key
  }`
  ),
  getServerAggregationQuery(
    'AgentVersion',
    `agentVersions {
    count
    key
  }`
  )
]

const serverQuickFiltersQuery = gql`
  query ServerQuickFilters(
    $organizationID: ID!
    $filterText: String
    $tags: [String]
    $applications: [String]
    $logLevels: [String]
    $environments: [String]
    $agentVersions: [String]
  ) {
    serverQuickFilters(
      organizationID: $organizationID
      filters: {
        tags: $tags
        applications: $applications
        logLevels: $logLevels
        environments: $environments
        agentVersions: $agentVersions
        filterText: $filterText
      }
    ) {
      id
      name
      count
    }
  }
`

const initialAggregationSelections: AggregationOptions<ServerAggregationCategory> =
  {
    tags: [],
    applications: [],
    logLevels: [],
    environments: [],
    agentVersions: []
  }

type ServerOrderField = 'NAME' | 'ENVIRONMENT' | 'LAST_SEEN' | 'VERSION'

export type ServerAggregationCategory =
  | 'tags'
  | 'applications'
  | 'logLevels'
  | 'environments'
  | 'agentVersions'

const aggregationConfigs: AggregationConfigs<ServerAggregationCategory> = {
  tags: {
    groupName: {
      id: 'title_tags_server',
      defaultMessage: 'Tags'
    }
  },
  applications: {
    groupName: {
      id: 'title_applications',
      defaultMessage: 'Applications'
    },
    bucketToID: ({ key: { id } }) => id,
    bucketToString: ({ key: { name } }) => name
  },
  logLevels: {
    groupName: {
      id: 'title_logLevels',
      defaultMessage: 'Log Levels'
    }
  },
  environments: {
    groupName: {
      id: 'title_environment_server',
      defaultMessage: 'Environment'
    },
    bucketToString: ({ key }, intl) =>
      intl.formatMessage({ id: environmentI18nConfig[key] })
  },
  agentVersions: {
    groupName: {
      id: 'title_serverOverview_agentVersion',
      defaultMessage: 'Agent Version'
    }
  }
}

export const orderFieldConfig: OrderFieldConfig<ServerOrderField> = {
  NAME: {
    direction: 'ASC',
    message: { id: 'title_name', defaultMessage: 'Name' }
  },
  ENVIRONMENT: {
    direction: 'ASC',
    message: { id: 'title_environment_server', defaultMessage: 'Environment' }
  },
  LAST_SEEN: {
    direction: 'DESC',
    message: { id: 'title_lastSeen_server', defaultMessage: 'Last Seen' }
  },
  VERSION: {
    direction: 'ASC',
    message: { id: 'title_version', defaultMessage: 'Version' }
  }
}

interface ServerQueryProps {
  readonly rowDataID: string
  readonly children: (rowData: Server) => React.ReactNode
}

const getServerQuery = memoize(
  (organizationID) =>
    function ServerRowQuery({
      rowDataID: serverID,
      children
    }: ServerQueryProps) {
      return (
        <Query
          query={serverQuery}
          fetchPolicy="cache-only"
          variables={{
            serverID,
            organizationID
          }}
        >
          {({ data }) => {
            if (!data) {
              return false
            }
            const {
              server: { applications, ...server }
            } = data
            return children({
              ...server,
              applications: applications.map(
                ({ language, ...application }) => ({
                  language: AgentLanguage.fromValue(language),
                  ...application
                })
              )
            })
          }}
        </Query>
      )
    }
)

function quickFilterToGroup() {
  return 'other'
}

export interface ContextValue {
  order: Order<ServerOrderField>
  filterText: string
  quickFilterID: string
  onSetSortField: (field: ServerOrderField) => void
  onSetSortDirection: (direction: OrderDirection) => void
  onFilterTextChange: (vaslue: string) => void
  onQuickFilterIDChange: (quickFilterID: string) => void
  onGroupToggle: (props: OnGroupToggleProps<ServerAggregationCategory>) => void
  onOptionToggle: (
    props: OnOptionToggleProps<ServerAggregationCategory>
  ) => void
  aggregationSelections: AggregationOptions<ServerAggregationCategory>
  onAggregationCategoriesClear: (
    categories: ServerAggregationCategory[]
  ) => void
  updateCellHeight: (rowDataID: string, height: number) => void
  registerListRef: (ref: any) => void
  maxCellHeightsByRow: MaxCellHeightsByRow
  recomputeRowHeights: () => void
  resetFilters: () => void
  serverUpdatingFeature: ServerUpdatingFeature | null
  setServerUpdatingFeature: (
    serverUpdatingFeature: ServerUpdatingFeature | null
  ) => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const ServerGridContext = React.createContext<ContextValue>({
  order: {
    field: 'NAME',
    direction: orderFieldConfig.NAME.direction
  },
  onSetSortField: noop,
  onSetSortDirection: noop,
  filterText: '',
  onFilterTextChange: noop,
  quickFilterID: 'OPEN',
  onQuickFilterIDChange: noop,
  aggregationSelections: initialAggregationSelections,
  onGroupToggle: noop,
  onOptionToggle: noop,
  onAggregationCategoriesClear: noop,
  updateCellHeight: noop,
  registerListRef: noop,
  maxCellHeightsByRow: {},
  recomputeRowHeights: noop,
  resetFilters: noop,
  serverUpdatingFeature: null,
  setServerUpdatingFeature: noop
})

export interface ServerUpdatingFeature {
  id: string
  feature: 'ASSESS' | 'PROTECT'
}

export function ServersGridProvider({ children }) {
  const [serverUpdatingFeature, setServerUpdatingFeature] =
    useState<ServerUpdatingFeature | null>(null)

  return (
    <GridStateManager
      defaultOrderField="NAME"
      orderFieldConfig={orderFieldConfig}
      filterType="servers"
    >
      {(state) => {
        return (
          <ServerGridContext.Provider
            value={{
              ...state,
              serverUpdatingFeature,
              setServerUpdatingFeature
            }}
          >
            {children}
          </ServerGridContext.Provider>
        )
      }}
    </GridStateManager>
  )
}

export function ServersGrid() {
  const queryClient = useQueryClient()

  return (
    <AgentFeaturesLoader
      render={({ features = [], loading }) => {
        return (
          !loading && (
            <ServerGridContext.Consumer>
              {({
                order,
                onSetSortField,
                onSetSortDirection,
                filterText,
                onFilterTextChange,
                quickFilterID,
                onQuickFilterIDChange,
                aggregationSelections,
                onGroupToggle,
                onOptionToggle,
                onAggregationCategoriesClear,
                updateCellHeight,
                registerListRef,
                maxCellHeightsByRow,
                recomputeRowHeights,
                serverUpdatingFeature,
                setServerUpdatingFeature
              }) => {
                return (
                  <GridLoader<
                    Server,
                    ServerOrderField,
                    ServerAggregationCategory
                  >
                    aggregationsQueries={aggregationQueries}
                    aggregationsQueryRootKey="serverAggregations"
                    batchSize={serverBatchSize}
                    quickFiltersQuery={serverQuickFiltersQuery}
                    quickFiltersQueryRootKey="serverQuickFilters"
                    collectionQuery={serverPageQuery}
                    collectionQueryRootKey="servers"
                    filterText={filterText}
                    order={order}
                    quickFilterID={quickFilterID}
                    aggregationBucketIDConfigs={aggregationConfigs}
                    aggregationFilters={aggregationSelections}
                  >
                    {({
                      totalRowCount,
                      loadMore,
                      organizationID,
                      quickFilters,
                      aggregations,
                      loadedRowCount,
                      rowDataValue,
                      rowGetter,
                      isRowLoaded,
                      organizationRole,
                      organizationType,
                      protectLicenseBreakdown,
                      protect,
                      refetchQueries,
                      refresh
                    }) => {
                      const ServerQuery = getServerQuery(organizationID)

                      const isFreemiumOrganization =
                        organizationType === 'Freemium'

                      return (
                        <LegacyGridContainer>
                          <Grid<
                            Server,
                            ServerOrderField,
                            ServerAggregationCategory
                          >
                            updateCellHeight={updateCellHeight}
                            registerListRef={registerListRef}
                            maxCellHeightsByRow={maxCellHeightsByRow}
                            RowQuery={ServerQuery}
                            loadedRowCount={loadedRowCount}
                            rowDataValue={rowDataValue}
                            isRowLoaded={isRowLoaded}
                            rowGetter={rowGetter}
                            loadMore={loadMore}
                            totalRowCount={totalRowCount}
                            rowHeight={(server) => {
                              const rowHeightFromApplicationCount =
                                getRowHeightFromApplicationCount(
                                  Math.min(
                                    server && server.applications
                                      ? server.applications.length
                                      : 0,
                                    maxApplicationsShownByDefault
                                  )
                                )
                              if (server && server.id) {
                                return (
                                  maxCellHeightsByRow[server.id] ||
                                  rowHeightFromApplicationCount
                                )
                              }
                              return rowHeightFromApplicationCount
                            }}
                            recomputeRowHeights={recomputeRowHeights}
                            renderBulkActions={(rowSelectionProps) =>
                              rowSelectionProps.filters &&
                              rowSelectionProps.filters.quickFilter &&
                              rowSelectionProps.filters.aggregations ? (
                                <BulkActionList
                                  organizationID={organizationID}
                                  refetchQueries={refetchQueries}
                                  refresh={refresh}
                                  organizationRole={organizationRole}
                                  isFreemium={isFreemiumOrganization}
                                  protect={protect}
                                  agentFeatures={features}
                                  {...rowSelectionProps}
                                  filters={{
                                    quickFilter:
                                      rowSelectionProps.filters.quickFilter,
                                    filterText:
                                      rowSelectionProps.filters.filterText,
                                    aggregations:
                                      rowSelectionProps.filters.aggregations
                                  }}
                                />
                              ) : undefined
                            }
                            titleMessage={{
                              id: 'title_servers',
                              defaultMessage: 'Servers'
                            }}
                            searchPlaceholderMessage={{
                              id: 'placeholder_findServer',
                              defaultMessage: 'Find Server'
                            }}
                            order={order}
                            onSetSortField={(value) => {
                              onSetSortField(value)
                              sendDefaultEvent('servers.grid.sort', value)
                            }}
                            onSetSortDirection={onSetSortDirection}
                            orderFieldConfig={orderFieldConfig}
                            onFilterTextChange={(value) => {
                              onFilterTextChange(value)
                              sendDefaultEvent('servers.grid.search', value)
                            }}
                            filterText={filterText}
                            onQuickFilterIDChange={onQuickFilterIDChange}
                            quickFilterID={quickFilterID}
                            quickFilters={quickFilters}
                            quickFilterToGroup={quickFilterToGroup}
                            aggregations={aggregations}
                            aggregationConfigs={aggregationConfigs}
                            aggregationSelections={aggregationSelections}
                            onGroupToggle={onGroupToggle}
                            onOptionToggle={onOptionToggle}
                            onAggregationCategoriesClear={
                              onAggregationCategoriesClear
                            }
                            batchSize={serverBatchSize}
                            gridDataValue={protectLicenseBreakdown}
                            columns={[
                              {
                                cellRenderer: getServerMetadataCellRenderer({
                                  organizationID,
                                  protect,
                                  isFreemiumOrganization
                                }),
                                loadingCellRenderer: loadingServerMetadataCell,
                                headerConfig: {
                                  message: {
                                    id: 'title_server'
                                  },
                                  aggregations: [
                                    'tags',
                                    'logLevels',
                                    'agentVersions'
                                  ]
                                },
                                width: 270,
                                minWidth: 205,
                                grow: 5,
                                horizontalCenter: false
                              },
                              {
                                cellRenderer: firstSeenCellRenderer,
                                loadingCellRenderer: loadingSeenCell,
                                headerConfig: {
                                  message: {
                                    id: 'title_fistSeen_server'
                                  }
                                },
                                width: 120,
                                minWidth: 120,
                                maxWidth: 120
                              },
                              {
                                cellRenderer: lastSeenCellRenderer,
                                loadingCellRenderer: loadingSeenCell,
                                headerConfig: {
                                  message: {
                                    id: 'title_lastSeen_server'
                                  }
                                },
                                width: 120,
                                minWidth: 120,
                                maxWidth: 120
                              },
                              {
                                cellRenderer: getEnvironmentCellRenderer({
                                  organizationRole,
                                  organizationID,
                                  refetchQueries,
                                  refresh
                                }),
                                loadingCellRenderer: loadingEnvironmentCell,
                                headerConfig: {
                                  message: {
                                    id: 'title_environment_server',
                                    defaultMessage: 'Environment'
                                  },
                                  aggregations: ['environments']
                                },
                                width: 175,
                                minWidth: 175,
                                maxWidth: 175,
                                horizontalCenter: false
                              },
                              {
                                cellRenderer: getApplicationsCellRenderer({
                                  organizationID,
                                  isListExpanded: (id) => {
                                    return (
                                      maxCellHeightsByRow[id] !== undefined &&
                                      maxCellHeightsByRow[id] > 0
                                    )
                                  }
                                }),
                                loadingCellRenderer:
                                  loadingApplicationsCellRenderer,
                                headerConfig: {
                                  message: {
                                    id: 'title_applications',
                                    defaultMessage: 'Applications'
                                  },
                                  aggregations: ['applications']
                                },
                                width: 170,
                                minWidth: 150,
                                maxWidth: 300,
                                horizontalCenter: false
                              },
                              {
                                cellRenderer: getAssessCellRenderer({
                                  organizationRole,
                                  agentFeatures: features,
                                  organizationID,
                                  refetchQueries,
                                  refresh,
                                  serverUpdatingFeature,
                                  setServerUpdatingFeature
                                }),
                                loadingCellRenderer:
                                  featureSwitchLoadingCellRenderer,
                                headerConfig: {
                                  message: {
                                    id: 'title_featureTypeAssess'
                                  }
                                },
                                width: 90,
                                minWidth: 90,
                                maxWidth: 90,
                                horizontalCenter: true
                              },
                              {
                                cellRenderer: getProtectCellRenderer({
                                  organizationRole,
                                  agentFeatures: features,
                                  organizationID,
                                  refetchQueries,
                                  refresh,
                                  queryClient,
                                  serverUpdatingFeature,
                                  setServerUpdatingFeature
                                }),
                                loadingCellRenderer:
                                  featureSwitchLoadingCellRenderer,
                                headerConfig: {
                                  message: {
                                    id: 'title_featureTypeProtect'
                                  }
                                },
                                width: 90,
                                minWidth: 90,
                                maxWidth: 90,
                                horizontalCenter: true,
                                isHidden: () => !protect
                              },
                              {
                                cellRenderer: getRowActionsCellRenderer({
                                  organizationID,
                                  organizationRole,
                                  isFreemiumOrganization,
                                  refresh,
                                  refetchQueries,
                                  protect,
                                  features
                                }),
                                loadingCellRenderer: () => <div />,
                                maxWidth: 140,
                                width: 140,
                                minWidth: 140
                              }
                            ]}
                          />
                        </LegacyGridContainer>
                      )
                    }}
                  </GridLoader>
                )
              }}
            </ServerGridContext.Consumer>
          )
        )
      }}
    />
  )
}
